import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import { Flex, Box } from 'reflexbox';
import {
  Heading,
  Text,
} from 'rebass';

import TalentTraits from 'shared/talentTraits';
import { getSpecInfoById } from 'shared/character';

import config from 'frontend/config';

import {
  CopyToClipboard,
  HeroTreeIcon,
  Icon,
  If,
  WowIcon,
} from 'frontend/components';

const colors = {
  selected: '#ffd100',
  available: '#40bf40',
  unavailable: '#434343',
  locked: '#121212',
  display: '#ffffff11',
  exclude: config.colors.error,
};

const getEdges = (nodes) => {
  const nodeLookup = _.keyBy(nodes, 'id');

  const edges = [];
  _.each(nodes, node => {
    _.each(node.next, next => {
      edges.push([
        node,
        nodeLookup[next],
      ]);
    });
  });

  return edges;
};

const getPointGroups = (nodes) => {
  let pointGroups = _.sortBy(_.map(
    _.groupBy(nodes, 'reqPoints'),
    (groupNodes, reqPoints) => {
      const points = _.sumBy(groupNodes, node => (node.freeNode ? 0 : node.rank));
      return {
        reqPoints: parseInt(reqPoints, 0) || 0,
        points,
        // 0 points is default valid
        // >0 points defaults to false and we flip to true in the next block if point totals are OK
        valid: !points,
      };
    },
  ), 'reqPoints');
  let total = 0;
  pointGroups = _.reduce(
    pointGroups,
    (result, pointGroup) => {
      const newGroup = { ...pointGroup };
      if (total >= pointGroup.reqPoints) {
        newGroup.valid = true;
      }
      total += newGroup.points;
      result.push(newGroup);
      return result;
    },
    [],
  );

  return pointGroups;
};

export function TraitIcon({
  node,
  entry,
  specId,
  size,
  noTooltip,
}) {
  const specInfo = getSpecInfoById(specId);

  if (!entry) {
    return null;
  }

  if (entry?.type === 'subtree') {
    return (
      <HeroTreeIcon
        subTreeId={entry.traitSubTreeId}
        size={size}
        style={{
          cursor: 'pointer',
        }}
      />
    );
  }

  return (
    <WowIcon
      icon={entry.icon}
      size={size}
      type="spell"
      id={noTooltip ? null : entry.spellId}
      noLink
      extra={{
        def: entry.definitionId,
        rank: node.rank,
        spellModifier: specInfo?.passiveSpellId,
      }}
    />
  );
}

TraitIcon.propTypes = {
  node: PropTypes.shape({
    type: PropTypes.string,
    entries: PropTypes.array,
    maxRanks: PropTypes.number,
    freeNode: PropTypes.bool,
    rank: PropTypes.number,
    chosenEntryId: PropTypes.number,
  }).isRequired,
  entry: PropTypes.shape({
    name: PropTypes.string,
    type: PropTypes.string,
    icon: PropTypes.string,
    spellId: PropTypes.number,
    definitionId: PropTypes.number,
    atlasMemberName: PropTypes.string,
    traitSubTreeId: PropTypes.number,
  }).isRequired,
  size: PropTypes.number.isRequired,
  specId: PropTypes.number.isRequired,
  noTooltip: PropTypes.bool,
};

TraitIcon.defaultProps = {
  noTooltip: false,
};

export function TalentTrait({
  node,
  size,
  borderSize,
  state,
  mini,
  showRanks,
  disableHover,
  specId,

  onClick,
  onContextMenu,
}) {
  const [hover, setHover] = React.useState(false);

  let content;
  let maxRanks = 1;
  let borderRadius = 0;
  let borderType = 'solid';
  let octagon = false;
  const octaClipPath = `polygon(
    30% 0%,
    70% 0%,
    100% 30%,
    100% 70%,
    70% 100%,
    30% 100%,
    0% 70%,
    0% 30%
  )`;

  if (mini) {
    content = null;
    borderRadius = size;
  } else if (node.type === 'single') {
    const entry = node.entries[0];
    if (entry.type === 'passive') {
      borderRadius = size;
    }
    maxRanks = node.maxRanks;
    content = (
      <Box
        onClick={() => onClick(entry)}
      >
        <TraitIcon
          node={node}
          entry={entry}
          specId={specId}
          size={size}
        />
      </Box>
    );
  } else if (node.type === 'choice') {
    octagon = true;
    let selectionContent;
    // this gets the octagon border about the right size to match the square and circle icons
    const innerSize = size - borderSize + 1;
    if (node.chosenEntryId) {
      const entry = _.find(node.entries, { id: node.chosenEntryId });
      selectionContent = (
        <Box
          style={{
            width: innerSize,
            height: innerSize,
          }}
        >
          <TraitIcon
            node={node}
            entry={entry}
            specId={specId}
            size={innerSize}
            noTooltip={!disableHover}
          />
        </Box>
      );
    } else {
      selectionContent = _.map(node.entries, (entry, i) => (
        <Box
          key={entry.id}
          style={{
            overflow: 'hidden',
            width: innerSize / 2,
            height: innerSize,
            borderRight: `${borderSize}px solid gray`,
            marginLeft: i, // account for border on left slice
          }}
        >
          <TraitIcon
            node={node}
            entry={entry}
            specId={specId}
            size={innerSize}
            noTooltip
          />
        </Box>
      ));
    }
    content = (
      <Flex
        align="center"
        justify="center"
        style={{
          WebkitClipPath: octaClipPath,
          position: 'relative',
        }}
      >
        {selectionContent}
      </Flex>
    );
  } else if (node.type === 'subtree') {
    borderRadius = size;
    let selectionContent;
    const innerSize = size - borderSize + 1;
    if (node.chosenEntryId) {
      const entry = _.find(node.entries, { id: node.chosenEntryId });
      selectionContent = (
        <Box
          style={{
            width: innerSize,
            height: innerSize,
          }}
        >
          <TraitIcon
            node={node}
            entry={entry}
            specId={specId}
            size={innerSize}
            noTooltip={!disableHover}
          />
        </Box>
      );
    } else {
      selectionContent = _.map(node.entries, (entry, i) => (
        <Box
          key={entry.id}
          style={{
            overflow: 'hidden',
            width: innerSize / 2,
            height: innerSize,
            borderRight: `${borderSize}px solid gray`,
            marginLeft: i, // account for border on left slice
          }}
        >
          <TraitIcon
            node={node}
            entry={entry}
            specId={specId}
            size={innerSize}
            noTooltip={!disableHover}
          />
        </Box>
      ));
    }
    content = (
      <Flex
        align="center"
        justify="center"
        style={{
          borderRadius: innerSize,
          position: 'relative',
        }}
      >
        {selectionContent}
      </Flex>
    );
  } else {
    // eslint-disable-next-line no-console
    console.warn('unknown node type', node.type);
  }

  if (state === 'locked') {
    content = (
      <div style={{ filter: 'brightness(0.3) grayscale(1)' }}>
        {content}
      </div>
    );
  } else if (state === 'unavailable' || state === 'exclude') {
    content = (
      <div style={{ filter: 'grayscale(1)' }}>
        {content}
      </div>
    );
  }

  if (state === 'available') {
    borderType = 'dotted';
  }

  if (node.freeNode) {
    maxRanks = 0;
  }
  return (
    <div
      style={{ position: 'relative' }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <Flex
        align="center"
        justify="center"
        style={{
          border: `${borderSize}px ${borderType} ${colors[state]}`,
          backgroundColor: colors[state],
          lineHeight: 1,
          borderRadius,
          overflow: 'hidden',
          height: size + 2,
          width: size + 2,
          WebkitClipPath: octagon ? octaClipPath : null,
        }}
        onContextMenu={onContextMenu}
      >
        {content}
      </Flex>
      <If
        condition={((showRanks && !node.freeNode && node.type !== 'subtree') || maxRanks > 1) && !mini}
        then={() => (
          <div
            style={{
              position: 'absolute',
              right: -4,
              bottom: -4,
              background: '#000',
              fontFamily: 'Tahoma',
              fontSize: size * 0.35,
              paddingLeft: 2,
              paddingRight: 2,
              pointerEvents: 'none',
            }}
          >
            {node.rank || 0}/{maxRanks}
          </div>
        )}
      />
      <If
        condition={state === 'exclude'}
        then={() => (
          <Icon
            name="close"
            size={1.5 * size}
            color={colors[state]}
            style={{
              pointerEvents: 'none',
              position: 'absolute',
              right: '50%',
              top: '50%',
              transform: 'translate(50%, -50%)',
            }}
          />
        )}
      />
      <If
        condition={_.includes(['choice', 'subtree'], node.type)}
        then={() => {
          const arrowSize = size / 2;
          return (
            <>
              <Icon
                name="triangleRight"
                size={arrowSize}
                color={colors[state]}
                style={{
                  position: 'absolute',
                  right: -(arrowSize) + 3,
                  top: '50%',
                  transform: 'translate(0%, -50%)',
                }}
              />
              <Icon
                name="triangleLeft"
                size={arrowSize}
                color={colors[state]}
                style={{
                  position: 'absolute',
                  left: -(arrowSize) + 3,
                  top: '50%',
                  transform: 'translate(0%, -50%)',
                }}
              />
            </>
          );
        }}
      />
      <If
        condition={!disableHover && hover && _.includes(['choice', 'subtree'], node.type)}
        then={() => {
          const hoverSize = _.round(size * 1.2);
          const offset = 30;
          const width = (hoverSize * 2) + offset + (2 * borderSize);
          return (
            <Flex
              justify="space-between"
              align="center"
              style={{
                position: 'absolute',
                width,
                height: 1.4 * hoverSize,
                top: -hoverSize - 1,
                padding: 5,
                left: -(width / 4) - (offset / 4),
                backgroundColor: '#434343ee',
                zIndex: 20,
                borderRadius: 10,
              }}
            >
              {_.map(node.entries, (entry) => (
                <Box
                  key={entry.id}
                  style={{
                    width: hoverSize + (2 * borderSize),
                    height: hoverSize + (2 * borderSize),
                    border: entry.id === node.chosenEntryId ? `${borderSize}px ${borderType} ${colors[state]}` : '',
                  }}
                  onClick={() => onClick(entry)}
                >
                  <TraitIcon
                    node={node}
                    entry={entry}
                    specId={specId}
                    size={hoverSize}
                  />
                </Box>
              ))}
            </Flex>
          );
        }}
      />
    </div>
  );
}

TalentTrait.propTypes = {
  node: PropTypes.shape({
    type: PropTypes.string,
    entries: PropTypes.array,
    maxRanks: PropTypes.number,
    freeNode: PropTypes.bool,
    rank: PropTypes.number,
    chosenEntryId: PropTypes.number,
  }).isRequired,
  size: PropTypes.number.isRequired,
  borderSize: PropTypes.number,
  state: PropTypes.string,
  mini: PropTypes.bool,
  showRanks: PropTypes.bool,
  disableHover: PropTypes.bool,
  specId: PropTypes.number,

  onClick: PropTypes.func,
  onContextMenu: PropTypes.func,
};

TalentTrait.defaultProps = {
  borderSize: 2,
  state: 'unavailable',
  mini: false,
  showRanks: false,
  disableHover: false,
  specId: null,

  onClick: () => {},
  onContextMenu: () => {},
};

function parseForExport(selected, specId) {
  const rawString = _.join(_.map(selected, ({ entryId, rank }) => _.join([entryId, rank], ':')), '/');
  return TalentTraits.parse(rawString, specId);
}

function TalentTree({
  specId,
  level,
  width,
  talents,
  exclude,
  showRanks,
  allowExclude,
  hideExport,
  hideHeader,
  mini,
  hideFooter,
  strokeWidth,
  readOnly,
  name,

  onChange,
}) {
  if (_.isString(talents) && talents.length === 7) {
    return null;
  }

  const [selected, setSelected] = React.useState([]);
  const [excluded, setExcluded] = React.useState([]);
  const [invalidTraits, setInvalidTraits] = React.useState(false);


  const tree = TalentTraits.getTreeBySpecId(specId);

  // heuristic value to get icons sized correctly based on scaled column size
  const iconScale = 0.52;
  const paddingPct = 0.02;

  const stroke = mini ? 1 : strokeWidth;

  const showHeroNodes = !mini && level > 70;

  const {
    classNodes,
    specNodes,
    subTreeNodes,
    heroNodes,
  } = TalentTraits.getDisplayAdjustedNodes(specId, showHeroNodes);
  const allNodes = _.concat(classNodes, specNodes, subTreeNodes, heroNodes);

  React.useEffect(() => {
    if (talents) {
      const parsed = TalentTraits.parse(talents, specId);
      if (parsed) {
        setSelected(
          _.map(
            parsed.entries,
            entry => ({ nodeId: entry.entry.nodeId, entryId: entry.entry.id, rank: entry.rank }),
          ),
        );
        if (allowExclude) {
          setExcluded(exclude);
        } else {
          setExcluded([]);
        }

        setInvalidTraits(parsed.unknownEntries.length > 0);
      }
    }
  }, [talents, allowExclude]);

  if (allNodes.length === 0) {
    // console.log('SOFT WARNING: No talent nodes found for spec', specId);
    return null;
  }

  const callOnChange = (newSelected, newExclude) => {
    if (readOnly) {
      return;
    }
    if (onChange) {
      onChange(parseForExport(newSelected, specId), newExclude);
    }
  };

  const bounds = TalentTraits.getBounds(allNodes);
  const classBounds = TalentTraits.getBounds(classNodes);
  const specBounds = TalentTraits.getBounds(specNodes);
  const heroBounds = TalentTraits.getBounds(heroNodes);

  const columnWidth = width / bounds.numColumns;
  let iconSize = _.round(columnWidth * iconScale);
  if (mini) {
    iconSize *= 0.7;
  }

  const usableWidth = width - iconSize;
  const paddingAmount = usableWidth * paddingPct;
  const actualWidth = usableWidth - (2 * paddingAmount);

  const scale = actualWidth / bounds.xRange;
  const height = (bounds.yRange * scale) + iconSize + (2 * paddingAmount);

  function scaleCoords([x, y]) {
    return [
      ((x - bounds.xMin) * scale) + paddingAmount,
      ((y - bounds.yMin) * scale) + paddingAmount,
    ];
  }

  const [classMiddleRaw] = scaleCoords([classBounds.xMin + (classBounds.xRange / 2), 0]);
  const [specMiddleRaw] = scaleCoords([specBounds.xMin + (specBounds.xRange / 2), 0]);
  const [heroMiddleRaw] = scaleCoords([heroBounds.xMin + (heroBounds.xRange / 2), 0]);
  const classMiddle = classMiddleRaw + (iconSize / 2);
  const specMiddle = specMiddleRaw + (iconSize / 2);
  const heroMiddle = heroMiddleRaw + (iconSize / 2);

  const totalPoints = TalentTraits.getPointsForLevel(level);
  const spentPoints = {
    class: 0,
    spec: 0,
  };

  // determine which subtree is active
  const allNodesLookup = _.keyBy(allNodes, 'id');
  const subTreeNodesSelected = _.map(
    _.filter(selected, sel => allNodesLookup[sel.nodeId]?.type === 'subtree'),
    sel => {
      const nodeInfo = allNodesLookup[sel.nodeId];
      const entryInfo = _.find(nodeInfo.entries, { id: sel.entryId });
      return {
        ...sel,
        node: nodeInfo,
        entry: entryInfo,
      };
    },
  );
  const activeSubtreeId = _.first(_.map(subTreeNodesSelected, 'entry.traitSubTreeId'));
  const activeSubtreeEntry = _.first(
    _.filter(_.map(subTreeNodes, stn => _.find(stn.entries, { traitSubTreeId: activeSubtreeId }))),
  );

  const nodeGroups = [
    'class',
    'spec',
    'subtree',
    'hero',
  ];
  _.each([classNodes, specNodes, subTreeNodes, heroNodes], (nodes, idx) => {
    const whichTree = nodeGroups[idx];
    const nodeLookup = _.keyBy(nodes, 'id');
    const treeSelected = _.filter(selected, s => {
      if (whichTree === 'hero') {
        return allNodesLookup[s.nodeId]?.subTreeId === activeSubtreeId;
      }
      return nodeLookup[s.nodeId];
    });

    const spent = _.sumBy(treeSelected, 'rank');
    spentPoints[whichTree] = spent;

    const pointsAvailable = spent < totalPoints[whichTree];
    const availableState = (pointsAvailable && !readOnly && !mini) ? 'available' : 'unavailable';

    /* eslint-disable no-param-reassign */
    // NOTE: This section mutates the trees to prevent a bunch of additional work
    // This is why we _.cloneDeep earlier.
    _.each(nodes, node => {
      const selection = _.find(selected, { nodeId: node.id });

      node.rank = 0;

      if (node.entryNode) {
        node.state = availableState;
      }

      if (node.reqPoints && node.reqPoints > spent) {
        node.state = 'locked';
      } else if (node.freeNode) {
        node.state = 'selected';
        node.rank = node.maxRanks;
      }

      if (selection) {
        node.state = 'selected';
        node.rank = selection.rank || 1;
        if (node.entries.length > 1) {
          node.chosenEntryId = selection.entryId;
        }
      }

      if (_.includes(excluded, node.id)) {
        node.state = 'exclude';
      }

      if (node.subTreeId && activeSubtreeId !== node.subTreeId) {
        node.state = 'hidden';
      }

      _.each(node.next, next => {
        if (node.state === 'selected' && node.rank === node.maxRanks) {
          nodeLookup[next].state = availableState;
        }
      });
    });
  });

  return (
    <Box style={{ width }}>
      <If
        condition={!mini && !hideHeader}
        then={() => (
          <Box style={{ position: 'relative', width, height: 30 }}>
            <div
              style={{
                position: 'absolute',
                left: classMiddle,
                transform: 'translateX(-50%)',
                whiteSpace: 'nowrap',
              }}
            >
              <Text bold>
                {tree.className}: {spentPoints.class} / {totalPoints.class}
              </Text>
            </div>
            <If
              condition={activeSubtreeEntry}
              then={() => (
                <div
                  style={{
                    position: 'absolute',
                    left: heroMiddle,
                    transform: 'translateX(-50%)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Text bold>
                    {activeSubtreeEntry.name}: {spentPoints.hero} / {totalPoints.hero}
                  </Text>
                </div>
              )}
            />
            <div
              style={{
                position: 'absolute',
                left: specMiddle,
                transform: 'translateX(-50%)',
                whiteSpace: 'nowrap',
              }}
            >
              <Text bold>
                {tree.specName}: {spentPoints.spec} / {totalPoints.spec}
              </Text>
            </div>
          </Box>
        )}
      />
      <Box
        style={{
          position: 'relative',
          width,
          height,
        }}
        onContextMenu={(e) => {
          e.preventDefault();
        }}
      >
        {_.map([classNodes, specNodes, subTreeNodes, heroNodes], (nodes, idx) => {
          const whichTree = nodeGroups[idx];
          const nodeLookup = _.keyBy(nodes, 'id');
          const pointsAvailable = spentPoints[whichTree] < totalPoints[whichTree];

          const edges = getEdges(nodes);
          return (
            <React.Fragment key={whichTree}>
              {_.map(nodes, node => {
                const [x, y] = scaleCoords([node.posX, node.posY]);
                const selection = _.find(selected, { nodeId: node.id });

                if (node.state === 'hidden') {
                  return null;
                }

                const onClick = (clickedEntry) => {
                  if (readOnly) {
                    return;
                  }

                  if (mini) {
                    // console.log('no action, mini mode');
                    return;
                  }

                  if (node.freeNode) {
                    // console.log('no action, free node');
                    return;
                  }

                  if (allowExclude && node.state === 'exclude') {
                    const newExcluded = _.without(exclude, node.id);
                    setExcluded(newExcluded);
                    callOnChange(selected, newExcluded);
                  } else if (
                    _.includes(['choice', 'subtree'], node.type)
                    && node.state === 'selected'
                    && node.chosenEntryId !== clickedEntry.id
                  ) {
                    // switch choice node selection
                    const newState = [
                      ..._.reject(selected, { nodeId: node.id }),
                      {
                        nodeId: node.id,
                        rank: node.rank,
                        entryId: clickedEntry.id,
                      },
                    ];
                    setSelected(newState);
                    callOnChange(newState, exclude);
                  } else if (pointsAvailable) {
                    if (node.state === 'available' || node.state === 'selected') {
                      if (!selection || selection.rank < node.maxRanks) {
                        const newState = [
                          ..._.reject(selected, { nodeId: node.id }),
                          {
                            nodeId: node.id,
                            rank: node.rank + 1,
                            entryId: clickedEntry.id,
                          },
                        ];
                        setSelected(newState);
                        callOnChange(newState, exclude);
                      } else {
                        // console.log('no action, at max rank');
                      }
                    } else {
                      // console.log('no action, not available');
                    }
                  } else {
                    // console.log('no action, max points spent');
                  }
                };

                const onContextMenu = () => {
                  if (readOnly) {
                    return;
                  }
                  if (mini) {
                    return;
                  }
                  if (node.freeNode) {
                    return;
                  }
                  if (allowExclude && node.rank === 0) {
                    const newExcluded = [
                      ...excluded,
                      node.id,
                    ];
                    setExcluded(newExcluded);
                    callOnChange(selected, newExcluded);
                  } else if (node.rank > 0) {
                    // for this check, temporarily reduce the rank
                    // eslint-disable-next-line no-param-reassign
                    node.rank -= 1;

                    let canDeselect = true;

                    // check to see if connected nodes require this one
                    _.each(node.next, nextId => {
                      // look at all next nodes
                      const nextNode = nodeLookup[nextId];
                      if (nextNode.rank > 0) {
                        // if there's only one way into the child, can't deselect the current node
                        if (nextNode.prev.length === 1) {
                          canDeselect = false;
                          // can stop iterating
                          return false;
                        }

                        // now look at all other incoming nodes to see if they are filled
                        let hasAnotherPath = false;
                        _.each(nextNode.prev, prevId => {
                          const prevNode = nodeLookup[prevId];
                          if (prevNode.id !== node.id && prevNode.rank === prevNode.maxRanks) {
                            hasAnotherPath = true;
                          }
                        });
                        if (!hasAnotherPath) {
                          canDeselect = false;
                          return false;
                        }
                      }
                      return true;
                    });

                    // check to see if this would break a point gate
                    const currentPointGroups = getPointGroups(nodes);
                    if (_.filter(currentPointGroups, { valid: false }).length > 0) {
                      canDeselect = false;
                    }

                    // eslint-disable-next-line no-param-reassign
                    node.rank += 1;

                    if (canDeselect) {
                      let newState = _.reject(selected, { nodeId: node.id });
                      if (node.rank - 1 > 0) {
                        // only re-add the node if it was a multiple point node and there are still points
                        newState.push({
                          nodeId: node.id,
                          rank: node.rank - 1,
                          // this can only happen for multi-rank nodes which are always single choice
                          entryId: node.entries[0].id,
                        });
                      }

                      // do a bunch of work to determine if we need to clear out 8- or 20-point groups

                      const stateWithNodes = _.filter(
                        _.map(newState, sel => ({ ...sel, node: nodeLookup[sel.nodeId] })),
                        'node',
                      );

                      const newStatePointGroups = getPointGroups(_.map(stateWithNodes, 'node'));
                      const newStatInvalidGroups = _.filter(newStatePointGroups, { valid: false });

                      if (newStatInvalidGroups.length > 0) {
                        const invalidReqPoints = new Set(_.map(newStatInvalidGroups, 'reqPoints'));
                        newState = _.reject(newState, sel => {
                          const n = nodeLookup[sel.nodeId];
                          if (n) {
                            return invalidReqPoints.has(n.reqPoints);
                          }

                          // not found, must be in other tree
                          return false;
                        });
                      }

                      setSelected(newState);
                      callOnChange(newState, exclude);
                    }
                  }
                };

                return (
                  // eslint-disable-next-line
                  <div
                    id={`node-${node.id}`}
                    key={node.id}
                    style={{
                      position: 'absolute',
                      top: y,
                      left: x,
                      height: iconSize + 2,
                      zIndex: 6,
                    }}
                  >
                    <TalentTrait
                      node={node}
                      size={whichTree === 'subtree' ? 2 * iconSize : iconSize}
                      borderSize={stroke}
                      state={node.state}
                      mini={mini}
                      showRanks={showRanks}
                      disableHover={readOnly}
                      specId={specId}
                      onClick={onClick}
                      onContextMenu={onContextMenu}
                    />
                  </div>
                );
              })}
              <svg
                style={{
                  position: 'absolute',
                  zIndex: 5,
                  width: '100%',
                  height: '100%',
                }}
              >
                {_.map(edges, ([a, b]) => {
                  const [ax, ay] = scaleCoords([a.posX, a.posY]);
                  const [bx, by] = scaleCoords([b.posX, b.posY]);
                  // This feels like it might be a future off-by-one error
                  // This was needed to get edges to line up properly when using mini mode
                  const edgeOffset = 1 + (iconSize / 2);
                  let state = 'unavailable';
                  if (b.state === 'locked') {
                    state = 'locked';
                  } else if (a.state === 'selected' && a.rank === a.maxRanks) {
                    if (b.state === 'selected' || pointsAvailable) {
                      state = 'selected';
                    }
                  }
                  return (
                    <line
                      key={`line-${specId}-${whichTree}-${a.id}-${b.id}`}
                      stroke={colors[state]}
                      strokeWidth={stroke}
                      strokeOpacity={0.6}
                      x1={ax + edgeOffset}
                      y1={ay + edgeOffset}
                      x2={bx + edgeOffset}
                      y2={by + edgeOffset}
                    />
                  );
                })}
              </svg>
            </React.Fragment>
          );
        })}
      </Box>
      <If
        condition={name}
        then={() => (
          <Heading level={5} align="center">{name}</Heading>
        )}
      />
      <If
        condition={!mini && !hideExport && !allowExclude}
        then={() => (
          <Flex justify="center">
            <CopyToClipboard
              tiny
              value={TalentTraits.stringify(parseForExport(selected, specId))}
            />
          </Flex>
        )}
      />
      <If
        condition={mini && !hideFooter}
        then={() => {
          const traits = TalentTraits.parse(talents, specId);
          const abilities = _.reject(traits?.entries, { entry: { type: 'passive' } });
          const rows = [{
            name: 'hero',
            size: 22,
            nodes: _.union(
              // include the subtree itself first
              _.filter(traits?.entries, { entry: { type: 'subtree' } }),
              _.filter(traits?.entries, ({ entry }) => {
                return entry.tree === 'hero'
                  && _.includes(traits.activeSubtrees, entry.node.subTreeId)
                  && entry.node.type === 'choice';
              }),
            ),
          }, {
            name: 'spec',
            size: 16,
            nodes: _.filter(abilities, { entry: { tree: 'spec' } }),
          }, {
            name: 'class',
            size: 16,
            nodes: _.filter(abilities, { entry: { tree: 'class' } }),
          }];
          return _.map(rows, ({ nodes, size, name: rowName }) => {
            return (
              <Flex wrap mt={1} key={rowName} justify="center">
                {_.map(nodes, node => (
                  <Box
                    key={node.entry.id}
                    style={{
                      height: size,
                      marginTop: 2,
                      marginRight: 5,
                    }}
                  >
                    <TraitIcon
                      node={node}
                      entry={node.entry}
                      specId={specId}
                      size={size}
                    />
                  </Box>
                ))}
              </Flex>
            );
          });
        }}
      />
      <If
        condition={invalidTraits}
        then={() => (
          <Text bold small align="center" color={config.colors.red}>* Unknown/outdated talents</Text>
        )}
      />
    </Box>
  );
}

TalentTree.propTypes = {
  specId: PropTypes.number.isRequired,
  level: PropTypes.number.isRequired,
  width: PropTypes.number,
  talents: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  exclude: PropTypes.array,
  strokeWidth: PropTypes.number,
  mini: PropTypes.bool,
  showRanks: PropTypes.bool,
  hideExport: PropTypes.bool,
  hideHeader: PropTypes.bool,
  allowExclude: PropTypes.bool,
  hideFooter: PropTypes.bool,
  readOnly: PropTypes.bool,
  name: PropTypes.string,

  onChange: PropTypes.func,
};

TalentTree.defaultProps = {
  width: 500,
  talents: '',
  exclude: [],
  strokeWidth: 2,
  mini: false,
  showRanks: true,
  hideExport: false,
  allowExclude: false,
  readOnly: false,
  hideFooter: false,
  hideHeader: false,
  name: null,

  onChange: null,
};

export default TalentTree;
